import type { Doc } from '@doc';
import { redirectEdgeMutation, reverseEdgeMutation } from '@doc';
import type { Point } from '@geom';
import type { EdgeDirection, Edges, NID } from '@graph';
import { expectDefined } from '@utils';
import type { CommandContext } from './CommandContext';
import { revealBounds } from './zoom.commands';

export function canReverseEdges(edges: Edges): boolean {
  return edges.size > 0;
}

export interface ReverseEdgesResult {
  doc: Doc;
  pan: Point;
  zoom: number;
}

export function reverseEdges(
  edges: Edges,
  context: CommandContext
): ReverseEdgesResult | undefined {
  if (!canReverseEdges(edges)) {
    return;
  }
  let { doc } = context;
  const { layouter } = context;

  const oldDoc = doc;

  try {
    doc = doc.produce('reverseEdges', draft => {
      const doc = draft as Doc;
      for (const edge of edges) {
        reverseEdgeMutation(doc, edge);
      }
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    const { pan, zoom } = revealBounds(
      expectDefined(doc.elementsBounds(context.selection)),
      context
    );
    const result = { doc, pan, zoom };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return { doc: oldDoc, pan: context.pan, zoom: context.zoom };
  }
}

export interface RedirectEdgesResult {
  doc: Doc;
  pan: Point;
  zoom: number;
}

export function redirect(
  edges: Edges,
  direction: EdgeDirection,
  destinationNode: NID,
  context: CommandContext
): RedirectEdgesResult | undefined {
  if (edges.size === 0) {
    return;
  }
  let { doc } = context;
  const { layouter } = context;

  const oldDoc = doc;

  try {
    doc = doc.produce('redirect', draft => {
      const doc = draft as Doc;
      for (const edge of edges) {
        redirectEdgeMutation(doc, { edge, direction }, destinationNode);
      }
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    const { pan, zoom } = revealBounds(
      expectDefined(doc.elementsBounds(context.selection)),
      context
    );
    const result = { doc, pan, zoom };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}
